import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate } from "react-router-dom";
import { VelaBanner } from "./components/VelaBanner";
import { AccountsStrip } from "./components/AccountsStrip";
import { QuickActionsRow } from "./components/QuickActionsRow";
import { CashFlowChart } from "./components/CashFlowChart";
import { SectionStatusRow } from "./components/SectionStatusRow";
import { OverviewEmptyState } from "./components/OverviewEmptyState";
import { SetupAccountingModal } from "./components/SetupAccountingModal";
import { AccountingOverviewHeader } from "./components/AccountingOverviewHeader";
import { useAccounts } from "../../../hooks/useAccounts";
import { useQuickActions } from "../../../hooks/useQuickActions";
import { useVelaAccountingSummary } from "../../../hooks/useVelaAccountingSummary";
import { useCashFlow } from "../../../hooks/useCashFlow";
import { useSectionStatus } from "../../../hooks/useSectionStatus";
import illustrationSrc from "../../../assets/illustrations/accounting-overview.svg";
import type {
  SetupOption,
  QuickAction,
  AccountSummary,
} from "./overviewTypes";

/**
 * Accounting Overview page.
 *
 * Sticky page header rendered by AppLayout via its pageHeader slot
 * (see AccountingOverviewHeader, re-exported from this file).
 *
 * Branches on account count (Model A shared bank list):
 *   State A, no accounts anywhere in Novala:
 *     OverviewEmptyState (hero + 1-2-3 steps + animation + trust row).
 *   State B, at least one connected account:
 *     Section-level dashboard (Vela banner + accounts strip +
 *     sage Quick Actions + cash flow chart + section status row).
 *
 * Nothing dead: every button navigates, every number flows through a
 * hook. Blocks hide themselves when their data is empty.
 *
 * Dev convenience: ?populated=1 in the URL flips every mock hook to
 * populated state for stakeholder demos.
 */

export { AccountingOverviewHeader };

const TOAST_DURATION_MS = 4000;

export default function AccountingOverview() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const { data: accounts, loading: accountsLoading } = useAccounts();
  const { data: quickActions } = useQuickActions();
  const { data: velaSummary } = useVelaAccountingSummary();
  const { data: cashFlow } = useCashFlow("this_year");
  const { data: sectionStatus } = useSectionStatus();

  const hasAccountsSetUp = accounts.length > 0;

  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const handleOpenSetup = useCallback(() => setModalOpen(true), []);
  const handleCloseSetup = useCallback(() => setModalOpen(false), []);

  const handleSetupSelect = useCallback(
    (option: SetupOption) => {
      setModalOpen(false);
      const paramMap: Record<SetupOption, string> = {
        connect_bank: "connect",
        upload_csv: "csv",
        enter_manually: "manual",
      };
      navigate(`/accounting/bank-transactions?action=${paramMap[option]}`);
    },
    [navigate],
  );

  const handleQuickAction = useCallback(
    (action: QuickAction) => navigate(action.route),
    [navigate],
  );

  const handleAccountClick = useCallback(
    (account: AccountSummary) => {
      navigate(`/accounting/bank-transactions?accountId=${account.id}`);
    },
    [navigate],
  );

  const handleReviewAll = useCallback(() => {
    navigate("/vela/inbox?filter=accounting");
  }, [navigate]);

  if (accountsLoading) {
    return (
      <div style={pageStyle}>
        <LoadingPlaceholder />
      </div>
    );
  }

  if (!hasAccountsSetUp) {
    return (
      <div style={pageStyle}>
        <OverviewEmptyState onSetUp={handleOpenSetup} />
        <SetupAccountingModal
          open={modalOpen}
          onClose={handleCloseSetup}
          onSelect={handleSetupSelect}
        />
        {toastMessage && <Toast message={toastMessage} />}
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <VelaBanner
        summary={velaSummary}
        onReviewAll={handleReviewAll}
        illustrationSrc={illustrationSrc}
      />
      <AccountsStrip
        accounts={accounts}
        onAccountClick={handleAccountClick}
        onAddAccount={handleOpenSetup}
      />
      <QuickActionsRow actions={quickActions} onActionClick={handleQuickAction} />
      <CashFlowChart data={cashFlow} />
      <SectionStatusRow status={sectionStatus} />

      <SetupAccountingModal
        open={modalOpen}
        onClose={handleCloseSetup}
        onSelect={handleSetupSelect}
      />
      {toastMessage && <Toast message={toastMessage} />}
    </div>
  );
}

interface ToastProps { message: string }
function Toast({ message }: ToastProps) {
  return (
    <div style={toastStyle} role="status" aria-live="polite">
      {message}
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <div style={loadingStyle} role="status" aria-live="polite">
      Loading your books...
    </div>
  );
}

const pageStyle: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  padding: 0,
  background: "#ffffff",
};

const loadingStyle: CSSProperties = {
  display: "flex", justifyContent: "center", alignItems: "center",
  minHeight: "calc(100vh - 180px)", fontSize: 13, color: "#6B7684",
};

const toastStyle: CSSProperties = {
  position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
  background: "#0E1A1A", color: "#ffffff",
  padding: "10px 16px", borderRadius: 8,
  fontSize: 13, fontWeight: 500,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.28)",
  zIndex: 1100,
};